import { useState, useEffect } from 'react';
import { Bell, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { useIsMobile } from '@/hooks/use-mobile';

interface BannerHeading {
  id: string;
  title: string;
  content: string;
}

interface BannerData {
  id: string;
  title: string;
  content: string;
  banner_text: string;
  pdf_url?: string;
  pdf_filename?: string;
  headings?: BannerHeading[];
  is_enabled: boolean;
  updated_at?: string;
}

const SEEN_KEY = 'seenBanners';

const NotificationBanner = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [bannerData, setBannerData] = useState<BannerData | null>(null);
  const [loading, setLoading] = useState(true);
  const [showDismissDialog, setShowDismissDialog] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    fetchBannerData();
  }, []);

  const bannerKey = (b: { id: string; updated_at?: string }) => `${b.id}:${b.updated_at ?? ''}`;

  const fetchBannerData = async () => {
    try {
      const { data, error } = await supabase
        .from('notification_banners')
        .select('*')
        .eq('is_enabled', true)
        .limit(1)
        .maybeSingle();

      if (error) {
        console.warn('Banner fetch failed silently:', error.message);
        setIsVisible(false);
        return;
      }

      if (data) {
        // Check if this banner (in its current version) was already opened/dismissed
        const seen: string[] = JSON.parse(localStorage.getItem(SEEN_KEY) || '[]');
        const dismissedBanners = JSON.parse(localStorage.getItem('dismissedBanners') || '[]');
        const key = bannerKey(data);
        const wasSeen = seen.includes(key) || dismissedBanners.includes(data.id);
        
        // Parse headings from JSON string if needed
        let parsedHeadings: BannerHeading[] = [];
        if (data.headings) {
          try {
            if (typeof data.headings === 'string') {
              parsedHeadings = JSON.parse(data.headings);
            } else if (Array.isArray(data.headings)) {
              parsedHeadings = data.headings as unknown as BannerHeading[];
            }
          } catch (error) {
            console.error('Error parsing headings:', error);
            parsedHeadings = [];
          }
        }
        
        setBannerData({
          ...data,
          headings: parsedHeadings
        });
        setIsVisible(true);
        setIsCollapsed(wasSeen);
      } else {
        setIsVisible(false);
      }
    } catch (error) {
      // Silently handle errors to prevent console noise
      setIsVisible(false);
    } finally {
      setLoading(false);
    }
  };

  const markSeen = () => {
    if (!bannerData) return;
    const seen: string[] = JSON.parse(localStorage.getItem(SEEN_KEY) || '[]');
    const key = bannerKey(bannerData);
    if (!seen.includes(key)) {
      seen.push(key);
      localStorage.setItem(SEEN_KEY, JSON.stringify(seen));
    }
  };

  const handleOpen = () => {
    setIsOpen(true);
    markSeen();
    setIsCollapsed(true);
  };

  const handleDismiss = () => {
    setShowDismissDialog(true);
  };

  const handleConfirmDismiss = () => {
    markSeen();
    setIsCollapsed(true);
    setShowDismissDialog(false);
  };

  const handleCancelDismiss = () => {
    setShowDismissDialog(false);
  };

  if (loading || !isVisible || !bannerData) return null;

  return (
    <>
      {isCollapsed ? (
        <button
          type="button"
          onClick={handleOpen}
          aria-label={bannerData.banner_text || 'Mitteilung öffnen'}
          className={cn(
            "fixed top-24 right-4 z-[50] mobile-menu-open:z-[20]",
            "banner-glow rounded-full border border-primary/30 bg-primary/10 p-2.5",
            "flex items-center justify-center"
          )}
        >
          <Bell className="banner-bell h-4 w-4 text-primary" />
        </button>
      ) : (
        <div 
          className={cn(
            "fixed top-20 w-full bg-primary/10 border-b border-primary/20 px-4 py-2 z-[50] mobile-menu-open:z-[20]",
            "banner-glow cursor-pointer"
          )}
          onClick={handleOpen}
        >
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-3 md:justify-center md:relative">
            <div className="flex items-center gap-3 flex-1 md:flex-none min-w-0 md:justify-center">
              <Bell className="banner-bell h-4 w-4 text-primary animate-bounce flex-shrink-0" />
              <span className="text-sm font-medium text-foreground truncate md:text-center">
                {bannerData.banner_text}
              </span>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0 md:absolute md:right-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDismiss();
                }}
                className="p-1 h-8 w-8 hover:bg-destructive/20 flex items-center justify-center"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          </div>
        </div>
      )}


      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-6xl max-h-[85vh] overflow-y-auto z-[100]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-primary" />
              {bannerData.title}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 text-sm">
            {/* Custom Content */}
            <div className="whitespace-pre-wrap text-muted-foreground">
              {bannerData.content.replace(/\\n/g, '\n').split('\n').map((line, index) => (
                <div key={index}>
                  {line.split(/(\*\*.*?\*\*)/).map((part, partIndex) => 
                    part.startsWith('**') && part.endsWith('**') ? (
                      <strong key={partIndex} className="font-semibold text-foreground">
                        {part.slice(2, -2)}
                      </strong>
                    ) : (
                      part
                    )
                  )}
                </div>
              ))}
            </div>

            {/* Dynamic Headings */}
            {bannerData.headings && bannerData.headings.map((heading) => (
              <div key={heading.id}>
                <h3 className="font-semibold text-base mb-3 text-primary">
                  {heading.title}
                </h3>
                <div className="space-y-2 text-muted-foreground whitespace-pre-wrap">
                  {heading.content.split('\n').map((line, index) => (
                    <div key={index}>
                      {line.split(/(\*\*.*?\*\*)/).map((part, partIndex) => 
                        part.startsWith('**') && part.endsWith('**') ? (
                          <strong key={partIndex} className="font-semibold text-foreground">
                            {part.slice(2, -2)}
                          </strong>
                        ) : (
                          part
                        )
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* PDF Display */}
            {bannerData.pdf_url && (
              <div className="border border-border rounded-lg overflow-hidden">
                <div className="bg-muted p-3 flex items-center gap-2">
                  <Bell className="h-4 w-4 text-primary" />
                  <span className="font-medium">{bannerData.pdf_filename || 'Dokument'}</span>
                </div>
                <iframe
                  src={bannerData.pdf_url}
                  className="w-full h-96"
                  title="Banner PDF"
                />
              </div>
            )}

            <div className="border-t pt-4">
              <p className="text-muted-foreground">
                Liebe Grüße und bis bald!<br />
                <strong>Prof. Alois Tanzmeister</strong><br />
                (Obmann ASC-Fechten Wien)
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dismiss Confirmation Dialog */}
      <Dialog open={showDismissDialog} onOpenChange={setShowDismissDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Banner ausblenden</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Möchten Sie dieses Banner dauerhaft ausblenden? Es wird erst wieder angezeigt, wenn der Inhalt geändert wird.
            </p>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={handleCancelDismiss}
                size="sm"
              >
                Abbrechen
              </Button>
              <Button
                onClick={handleConfirmDismiss}
                size="sm"
              >
                Ausblenden bis zum Update
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default NotificationBanner;